<template>
  <div class="sub-banner-container" v-if="!loading && visibleBanners.length > 0">
    <transition-group name="slide-down" tag="div" class="sub-banner-list">
      <div v-for="banner in visibleBanners" :key="banner.id" :class="['sub-banner', banner.bannerClass]" role="alert">
        <div class="sub-banner__inner">
          <!-- Ikon -->
          <v-icon :color="banner.iconColor" class="sub-banner__icon">{{ banner.icon }}</v-icon>

          <!-- Teks pesan -->
          <div class="sub-banner__text">
            <span class="sub-banner__title">{{ banner.title }}</span>
            <span class="sub-banner__msg">
              {{ banner.formattedMessage }}
            </span>
          </div>

          <!-- Sisa hari (pill kecil) -->
          <div v-if="banner.days_remaining !== null" class="sub-banner__pill">
            {{ banner.days_remaining >= 0 ? banner.days_remaining + ' hari tersisa' : 'Sudah berakhir' }}
          </div>

          <!-- Tombol tutup (hanya untuk status non-kritis) -->
          <button v-if="banner.dismissible" class="sub-banner__close" @click="dismissBanner(banner.id)" aria-label="Tutup">
            <v-icon small :color="banner.iconColor">mdi-close</v-icon>
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8084'

export default {
  name: 'SubscriptionBanner',
  data: () => ({
    statuses: [],
    loading: false,
    dismissedIds: [],
  }),

  computed: {
    token() {
      const userInfo = this.$store?.getters?.USER_INFO
      return userInfo?.token || localStorage.getItem('user-token') || ''
    },

    isibleBanners() {
      if (this.loading || !this.statuses.length) return [];
      
      return this.statuses
        .filter(s => {
          if (this.dismissedIds.includes(s.batch_name)) return false;
          const status = s.effective_status;
          const warnDays = s.warn_days_before ?? 30; // Menggunakan batas dari Admin (default 30)
          // 1. Status kritis (grace, expired, suspended) selalu tampil
          if (['grace', 'expired', 'suspended'].includes(status)) return true;
          // 2. Status 'active' maupun 'trial' HANYA tampil jika sisa hari <= warn_days_before
          if (['active', 'trial'].includes(status)) {
            return s.days_remaining !== null && s.days_remaining <= warnDays;
          }
          return false;
        })
        .map(s => {
          let bannerClass = 'sub-banner--info';
          if (s.effective_status === 'expired' || s.effective_status === 'suspended') bannerClass = 'sub-banner--danger';
          else if (s.effective_status === 'grace') bannerClass = 'sub-banner--warning';
          else if (s.effective_status === 'trial') bannerClass = 'sub-banner--trial';
          else if (s.days_remaining <= 7) bannerClass = 'sub-banner--warning';

          let icon = 'mdi-bell-ring-outline';
          if (s.effective_status === 'expired') icon = 'mdi-alert-octagon';
          else if (s.effective_status === 'suspended') icon = 'mdi-pause-octagon';
          else if (s.effective_status === 'grace') icon = 'mdi-clock-alert-outline';
          else if (s.effective_status === 'trial') icon = 'mdi-timer-sand';

          let iconColor = '#00e5ff';
          if (bannerClass === 'sub-banner--danger') iconColor = '#ff5252';
          else if (bannerClass === 'sub-banner--warning') iconColor = '#ffab40';
          else if (bannerClass === 'sub-banner--trial') iconColor = '#ce93d8';

          let title = 'Perhatian: Langganan Hampir Berakhir';
          if (s.effective_status === 'expired') title = 'Layanan Berakhir';
          else if (s.effective_status === 'suspended') title = 'Layanan Ditangguhkan';
          else if (s.effective_status === 'grace') title = 'Masa Tenggang Aktif';
          else if (s.effective_status === 'trial') title = 'Mode Trial';

          let rangeStr = '';
          if (s.valid_from && s.valid_until) {
            // Using a shorter date format for the banner title
            const formatShort = (dateStr) => {
              const d = new Date(dateStr);
              if (isNaN(d)) return dateStr;
              return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
            };
            rangeStr = ` (${formatShort(s.valid_from)} - ${formatShort(s.valid_until)})`;
          }

          title = `${title} — ${s.batch_name} (${s.active_device_count || 0} Device)${rangeStr}`;

          const dismissible = !['expired', 'suspended'].includes(s.effective_status);

          let formattedMessage = '';
          if (s.message) {
            formattedMessage = s.message.replace(
              /(\w{3}\s+\w{3}\s+\d{2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}.*?\))|([\d]{4}-[\d]{2}-[\d]{2}(?:T[\d:Z.+]+)?)/g,
              (match) => this.formatDate(match)
            );
          }

          return {
            ...s,
            id: s.batch_name,
            bannerClass,
            icon,
            iconColor,
            title,
            dismissible,
            formattedMessage
          };
        });
    },
  },

  methods: {
    dismissBanner(id) {
      this.dismissedIds.push(id);
    },

    // Format tanggal: "Selasa, 28 April 2027"
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      if (isNaN(d)) return dateStr
      return d.toLocaleDateString('id-ID', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },

    async fetchStatus() {
      if (!this.token) return
      this.loading = true
      try {
        const res = await axios.get(`${API_URL}/api/subscription/my-status`, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        const data = res.data?.data;
        if (Array.isArray(data)) {
          this.statuses = data;
        } else if (data) {
          this.statuses = [data];
        } else {
          this.statuses = [];
        }
      } catch (e) {
        // Gagal fetch → tidak tampilkan banner (fail silent)
        this.statuses = [];
      } finally {
        this.loading = false
      }
    }
  },

  mounted() {
    this.fetchStatus()
  }
}
</script>

<style scoped>
/* ===================================================
   Subscription Banner — bergaya dark glassmorphism
   sesuai tema Smart APJ
   =================================================== */
.sub-banner-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.sub-banner-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.sub-banner {
  width: 100%;
  border-radius: 10px;
  margin: 8px 0 4px;
  padding: 0;
  overflow: hidden;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255,255,255,0.08);
  transition: all 0.3s ease;
}

.sub-banner__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
}

/* Danger: expired / suspended */
.sub-banner--danger {
  background: rgba(183, 28, 28, 0.35);
  border-color: rgba(255, 82, 82, 0.45);
  box-shadow: 0 0 12px rgba(255, 82, 82, 0.2);
}

/* Warning: grace / sisa ≤ 30 hari */
.sub-banner--warning {
  background: rgba(230, 81, 0, 0.28);
  border-color: rgba(255, 171, 64, 0.45);
  box-shadow: 0 0 12px rgba(255, 171, 64, 0.15);
}

/* Info: sisa ≤ 30 hari tapi masih lama */
.sub-banner--info {
  background: rgba(0, 96, 100, 0.32);
  border-color: rgba(0, 229, 255, 0.35);
  box-shadow: 0 0 10px rgba(0, 229, 255, 0.1);
}

/* Trial: ungu / lavender */
.sub-banner--trial {
  background: rgba(74, 20, 140, 0.32);
  border-color: rgba(206, 147, 216, 0.45);
  box-shadow: 0 0 10px rgba(206, 147, 216, 0.15);
}

.sub-banner--trial .sub-banner__title {
  color: #ce93d8;
}

.sub-banner__icon {
  flex-shrink: 0;
}

.sub-banner__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1px;
}

.sub-banner__title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #ffe082;
}

.sub-banner--danger .sub-banner__title {
  color: #ff8a80;
}

.sub-banner--info .sub-banner__title {
  color: #80d8ff;
}

.sub-banner__msg {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.85);
  line-height: 1.3;
}

.sub-banner__pill {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  white-space: nowrap;
}

.sub-banner__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.sub-banner__close:hover {
  opacity: 1;
}

/* Animasi slide down */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0;
}
.slide-down-enter-to,
.slide-down-leave {
  max-height: 80px;
  opacity: 1;
}
</style>
